import React, {
  useState,
  useMemo,
} from "react"

import { ICONS } from "../Images"
import _ from "lodash"

import {
  Form,
  Button,
  ButtonToolbar,
  Schema,
  Checkbox,
  Message,
  toaster,
} from "rsuite"

import { useNavigate } from "react-router-dom"

import {
  useUserRegisterMutation,
  useUserLoginMutation,
} from "../Redux/actions/user.action"

import { Loader } from "../components/index"

import {
  Box,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Divider,
} from "@mui/material"

import {
  Visibility,
  VisibilityOff,
  ArrowForward,
  SchoolOutlined,
  SecurityOutlined,
  CheckCircleOutline,
} from "@mui/icons-material"

const { StringType } = Schema.Types

const PASSWORD_MIN_LENGTH = 8

/* =========================
   PASSWORD STRENGTH
========================= */

const getPasswordStrength = (password = "") => {
  if (!password) {
    return { score: 0, label: "", color: "#e2e8f0" }
  }

  let score = 0
  if (password.length >= PASSWORD_MIN_LENGTH) score++
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++
  if (/\d/.test(password)) score++
  if (/[^A-Za-z0-9]/.test(password)) score++

  const levels = [
    { label: "Too weak", color: "#ef4444" },
    { label: "Weak", color: "#f97316" },
    { label: "Fair", color: "#eab308" },
    { label: "Good", color: "#22c55e" },
    { label: "Strong", color: "#16a34a" },
  ]

  return { score, ...levels[score] }
}

/* =========================
   VALIDATION
========================= */

const model = Schema.Model({
  firstname: StringType()
    .isRequired("First name is required.")
    .maxLength(100, "First name must be 100 characters or fewer."),

  lastname: StringType()
    .maxLength(100, "Last name must be 100 characters or fewer."),

  email: StringType()
    .isEmail("Please enter a valid email.")
    .isRequired("Email is required.")
    .maxLength(100, "Email must be 100 characters or fewer."),

  password: StringType()
    .isRequired("Password is required.")
    .minLength(
      PASSWORD_MIN_LENGTH,
      `Password must be at least ${PASSWORD_MIN_LENGTH} characters.`
    ),

  confirm_password: StringType()
    .isRequired("Please confirm your password.")
    .addRule(
      (value, data) => value === data.password,
      "Passwords do not match."
    ),
})

const initialFormValue = {
  firstname: "",
  lastname: "",
  email: "",
  password: "",
  confirm_password: "",
}

export default function SignupPage() {
  const navigate = useNavigate()

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))

  /* =========================
     STATE
  ========================= */

  const [formValue, setFormValue] = useState(initialFormValue)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")

  const [
    userRegistrationAction,
    userRegisterState,
  ] = useUserRegisterMutation()

  const [
    loginUserAction,
    loginUserState,
  ] = useUserLoginMutation()

  const isSubmitting =
    userRegisterState.isLoading || loginUserState.isLoading

  const passwordStrength = useMemo(
    () => getPasswordStrength(formValue.password),
    [formValue.password]
  )

  /* =========================
     SIGNUP -> AUTO LOGIN
  ========================= */

  const handleSubmit = async (isValid) => {
    if (!isValid) return

    if (!acceptedTerms) {
      setErrorMessage("Please accept the terms of service to continue.")
      return
    }

    setErrorMessage("")

    const email = formValue.email.trim().toLowerCase()
    const { password } = formValue

    try {
      await userRegistrationAction({
        firstname: formValue.firstname.trim(),
        lastname: formValue.lastname.trim() || null,
        email,
        password,
        meta_data: {
          user_type: "user",
        },
      }).unwrap()
    } catch (error) {
      setErrorMessage(
        _.get(error, "data.detail") ||
        "Unable to create your account. Please try again."
      )
      return
    }

    try {
      const session = await loginUserAction({ email, password }).unwrap()

      localStorage.setItem("current_user", JSON.stringify(session))

      toaster.push(
        <Message showIcon type="success">
          Welcome, {formValue.firstname.trim()}! Your account is ready.
        </Message>,
        { placement: "topCenter" }
      )

      navigate("/")
    } catch (error) {
      // Account exists but auto-login failed: send them to sign in manually
      toaster.push(
        <Message showIcon type="info">
          Account created. Please sign in to continue.
        </Message>,
        { placement: "topCenter" }
      )

      navigate("/auth/login")
    }
  }

  const renderPasswordToggle = (visible, setVisible) => (
    <IconButton
      type="button"
      size="small"
      aria-label={visible ? "Hide password" : "Show password"}
      onClick={() => setVisible((prev) => !prev)}
      className="su-password-toggle"
    >
      {visible ? (
        <VisibilityOff fontSize="small" />
      ) : (
        <Visibility fontSize="small" />
      )}
    </IconButton>
  )

  return (
    <Box className="su-page">

      {/* ==========================================
                BACKGROUND DECORATION
            ========================================== */}

      <Box className="su-background">
        <Box className="su-blur su-blur-one" />
        <Box className="su-blur su-blur-two" />
      </Box>

      <Box className="su-container">

        {/* ======================================
                    LEFT BRANDING SECTION
                ====================================== */}

        <Box className="su-branding">

          <Box className="su-branding-image">
            <img
              src={ICONS.login_banner}
              alt="Education platform"
            />
          </Box>

          <Box className="su-branding-overlay" />

          <Box className="su-branding-content">

            <Box
              className="su-brand-logo"
              onClick={() => navigate("/")}
            >
              <Box className="su-logo-icon">
                <SchoolOutlined />
              </Box>

              <Typography className="su-brand-name">
                EduCare
              </Typography>
            </Box>

            <Box className="su-branding-text">

              <Typography className="su-branding-title">
                Start your
                <br />
                learning
                <br />
                journey.
              </Typography>

              <Typography className="su-branding-description">
                Create your account to set up your institution,
                manage classes, exams and fees, all in one place.
              </Typography>

            </Box>

            <Box className="su-feature-list">

              <Box className="su-feature-item">
                <CheckCircleOutline />
                <Typography>Free to get started</Typography>
              </Box>

              <Box className="su-feature-item">
                <CheckCircleOutline />
                <Typography>Set up in minutes</Typography>
              </Box>

              <Box className="su-feature-item">
                <CheckCircleOutline />
                <Typography>Secure & reliable platform</Typography>
              </Box>

            </Box>

          </Box>

        </Box>

        {/* ======================================
                    SIGNUP SECTION
                ====================================== */}

        <Box className="su-form-section">

          <Box className="su-form-container">

            {isMobile && (
              <Box
                className="su-mobile-brand"
                onClick={() => navigate("/")}
              >
                <Box className="su-mobile-logo-icon">
                  <SchoolOutlined />
                </Box>

                <Typography>EduCare</Typography>
              </Box>
            )}

            <Box className="su-form-header">

              <Typography className="su-form-title">
                Create account
              </Typography>

              <Typography className="su-form-subtitle">
                Fill in your details to get started
              </Typography>

            </Box>

            {errorMessage && (
              <Message
                showIcon
                type="error"
                closable
                onClose={() => setErrorMessage("")}
                className="su-error"
              >
                {errorMessage}
              </Message>
            )}

            <Form
              fluid
              model={model}
              formValue={formValue}
              onChange={setFormValue}
              onSubmit={handleSubmit}
              disabled={isSubmitting}
            >

              {/* NAME */}

              <Box className="su-name-row">

                <Form.Group className="su-form-group">
                  <Form.ControlLabel>First name</Form.ControlLabel>
                  <Form.Control
                    name="firstname"
                    placeholder="First name"
                    autoComplete="given-name"
                    size="lg"
                  />
                </Form.Group>

                <Form.Group className="su-form-group">
                  <Form.ControlLabel>
                    Last name <span className="su-optional">(optional)</span>
                  </Form.ControlLabel>
                  <Form.Control
                    name="lastname"
                    placeholder="Last name"
                    autoComplete="family-name"
                    size="lg"
                  />
                </Form.Group>

              </Box>

              {/* EMAIL */}

              <Form.Group className="su-form-group">
                <Form.ControlLabel>Email address</Form.ControlLabel>
                <Form.Control
                  name="email"
                  type="email"
                  placeholder="name@institution.com"
                  autoComplete="email"
                  size="lg"
                />
              </Form.Group>

              {/* PASSWORD */}

              <Form.Group className="su-form-group">
                <Form.ControlLabel>Password</Form.ControlLabel>

                <Box className="su-password-wrapper">
                  <Form.Control
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder={`At least ${PASSWORD_MIN_LENGTH} characters`}
                    autoComplete="new-password"
                    size="lg"
                  />
                  {renderPasswordToggle(showPassword, setShowPassword)}
                </Box>

                {formValue.password && (
                  <Box className="su-strength">
                    <Box className="su-strength-bars">
                      {[1, 2, 3, 4].map((level) => (
                        <Box
                          key={level}
                          className="su-strength-bar"
                          style={{
                            background:
                              passwordStrength.score >= level
                                ? passwordStrength.color
                                : "#e2e8f0",
                          }}
                        />
                      ))}
                    </Box>
                    <Typography
                      className="su-strength-label"
                      style={{ color: passwordStrength.color }}
                    >
                      {passwordStrength.label}
                    </Typography>
                  </Box>
                )}
              </Form.Group>

              {/* CONFIRM PASSWORD */}

              <Form.Group className="su-form-group">
                <Form.ControlLabel>Confirm password</Form.ControlLabel>

                <Box className="su-password-wrapper">
                  <Form.Control
                    name="confirm_password"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Re-enter your password"
                    autoComplete="new-password"
                    size="lg"
                  />
                  {renderPasswordToggle(
                    showConfirmPassword,
                    setShowConfirmPassword
                  )}
                </Box>
              </Form.Group>

              {/* TERMS */}

              <Checkbox
                checked={acceptedTerms}
                onChange={(_value, checked) => setAcceptedTerms(checked)}
                className="su-terms"
              >
                I agree to the Terms of Service and Privacy Policy
              </Checkbox>

              {/* SUBMIT */}

              <Form.Group className="su-submit-group">
                <ButtonToolbar>
                  <Button
                    appearance="primary"
                    block
                    type="submit"
                    className="su-submit-button"
                    loading={isSubmitting}
                  >
                    <span>Create account</span>
                    <ArrowForward fontSize="small" />
                  </Button>
                </ButtonToolbar>
              </Form.Group>

              <Box className="su-divider">
                <Divider />
                <Typography>OR</Typography>
                <Divider />
              </Box>

              <Box className="su-login-section">
                <Typography>Already have an account?</Typography>
                <Typography
                  component="span"
                  className="su-login-link"
                  onClick={() => navigate("/auth/login")}
                >
                  Sign in
                </Typography>
              </Box>

            </Form>

            <Box className="su-security-note">
              <SecurityOutlined />
              <Typography>
                Your information is securely protected.
              </Typography>
            </Box>

            <Typography className="su-copyright">
              © {new Date().getFullYear()} EduCare. All rights reserved.
            </Typography>

          </Box>

        </Box>

      </Box>

      {isSubmitting && <Loader show={true} />}

      {/* ==========================================
                STYLES
            ========================================== */}

      <style>{`

        .su-page {
          min-height: 100vh;
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          position: relative;
          overflow: hidden;
          background: linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #f8fafc 100%);
        }

        .su-page * {
          box-sizing: border-box;
        }

        .su-background {
          position: fixed;
          inset: 0;
          pointer-events: none;
          overflow: hidden;
        }

        .su-blur {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          opacity: 0.35;
        }

        .su-blur-one {
          width: 320px;
          height: 320px;
          background: #c7d2fe;
          top: -100px;
          right: -100px;
        }

        .su-blur-two {
          width: 350px;
          height: 350px;
          background: #bfdbfe;
          left: -120px;
          bottom: -100px;
        }

        /* =====================================
           MAIN CARD
        ===================================== */

        .su-container {
          width: 100%;
          max-width: 1180px;
          min-height: 760px;
          display: grid;
          grid-template-columns: 0.95fr 1.05fr;
          background: #ffffff;
          border-radius: 28px;
          overflow: hidden;
          position: relative;
          z-index: 2;
          box-shadow:
            0 25px 60px rgba(15, 23, 42, 0.12),
            0 5px 15px rgba(15, 23, 42, 0.05);
        }

        /* =====================================
           BRANDING
        ===================================== */

        .su-branding {
          position: relative;
          overflow: hidden;
          background: #312e81;
          color: white;
        }

        .su-branding-image {
          position: absolute;
          inset: 0;
        }

        .su-branding-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0.45;
        }

        .su-branding-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            145deg,
            rgba(49, 46, 129, 0.98),
            rgba(30, 27, 75, 0.82),
            rgba(15, 23, 42, 0.95)
          );
        }

        .su-branding-content {
          position: relative;
          z-index: 2;
          height: 100%;
          padding: 48px;
          display: flex;
          flex-direction: column;
        }

        .su-brand-logo {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          cursor: pointer;
          width: fit-content;
        }

        .su-logo-icon {
          width: 44px;
          height: 44px;
          border-radius: 13px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255,255,255,0.15);
          border: 1px solid rgba(255,255,255,0.2);
        }

        .su-brand-name {
          font-size: 22px !important;
          font-weight: 800 !important;
          letter-spacing: -0.5px;
        }

        .su-branding-text {
          margin-top: auto;
          margin-bottom: 40px;
          max-width: 480px;
        }

        .su-branding-title {
          font-size: clamp(2.4rem, 4vw, 4rem) !important;
          font-weight: 800 !important;
          line-height: 1.08 !important;
          letter-spacing: -2px !important;
          margin-bottom: 22px !important;
        }

        .su-branding-description {
          font-size: 16px !important;
          line-height: 1.7 !important;
          color: rgba(255,255,255,0.78);
          max-width: 420px;
        }

        .su-feature-list {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }

        .su-feature-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 9px 13px;
          border-radius: 10px;
          background: rgba(255,255,255,0.08);
          border: 1px solid rgba(255,255,255,0.1);
        }

        .su-feature-item svg {
          font-size: 17px;
        }

        .su-feature-item p {
          font-size: 12px;
          font-weight: 500;
        }

        /* =====================================
           FORM SECTION
        ===================================== */

        .su-form-section {
          display: flex;
          align-items: center;
          justify-content: center;
          background: #ffffff;
          padding: 44px 50px;
        }

        .su-form-container {
          width: 100%;
          max-width: 440px;
        }

        .su-form-header {
          margin-bottom: 26px;
        }

        .su-form-title {
          font-size: 34px !important;
          font-weight: 800 !important;
          color: #0f172a;
          letter-spacing: -1.5px;
          margin-bottom: 6px !important;
        }

        .su-form-subtitle {
          color: #64748b;
          font-size: 14px !important;
        }

        .su-error {
          margin-bottom: 18px;
          border-radius: 12px;
        }

        .su-name-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 14px;
        }

        .su-form-group {
          margin-bottom: 18px !important;
        }

        .su-optional {
          font-weight: 400;
          color: #94a3b8;
        }

        .su-page .rs-form-group .rs-form-control-label {
          font-size: 13px !important;
          font-weight: 650 !important;
          color: #334155 !important;
          margin-bottom: 8px !important;
        }

        .su-page .rs-form-control input,
        .su-page input.rs-input {
          background: #f8fafc !important;
          border: 1px solid #e2e8f0 !important;
          color: #0f172a !important;
          border-radius: 12px !important;
          font-size: 14px !important;
          transition: all .2s ease !important;
        }

        .su-page input.rs-input:hover {
          border-color: #cbd5e1 !important;
        }

        .su-page input.rs-input:focus {
          border-color: #6366f1 !important;
          background: #ffffff !important;
          box-shadow: 0 0 0 4px rgba(99,102,241,.10) !important;
        }

        /* =====================================
           PASSWORD
        ===================================== */

        .su-password-wrapper {
          position: relative;
          width: 100%;
        }

        .su-password-wrapper .rs-form-control {
          width: 100%;
        }

        .su-password-wrapper input.rs-input {
          padding-right: 50px !important;
        }

        .su-password-toggle {
          position: absolute !important;
          right: 5px;
          top: 24px;
          transform: translateY(-50%);
          z-index: 5;
          color: #64748b !important;
        }

        .su-password-toggle:hover {
          background: rgba(99, 102, 241, 0.08) !important;
        }

        .su-strength {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-top: 8px;
        }

        .su-strength-bars {
          flex: 1;
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 6px;
        }

        .su-strength-bar {
          height: 5px;
          border-radius: 4px;
          transition: background .2s ease;
        }

        .su-strength-label {
          font-size: 12px !important;
          font-weight: 600 !important;
          min-width: 60px;
          text-align: right;
        }

        /* =====================================
           TERMS + SUBMIT
        ===================================== */

        .su-terms {
          margin-left: -10px;
        }

        .su-terms label {
          font-size: 13px;
          color: #475569;
        }

        .su-submit-group {
          margin-top: 18px !important;
        }

        .su-submit-button {
          height: 50px !important;
          border-radius: 12px !important;
          background: linear-gradient(135deg, #6366f1, #4f46e5) !important;
          font-size: 14px !important;
          font-weight: 700 !important;
          display: flex !important;
          align-items: center;
          justify-content: center;
          gap: 8px;
          border: none !important;
          box-shadow: 0 8px 20px rgba(79,70,229,.25) !important;
          transition: all .2s ease !important;
        }

        .su-submit-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 12px 25px rgba(79,70,229,.3) !important;
        }

        .su-divider {
          display: flex;
          align-items: center;
          gap: 12px;
          margin: 24px 0 18px;
        }

        .su-divider .MuiDivider-root {
          flex: 1;
        }

        .su-divider p {
          font-size: 11px;
          font-weight: 600;
          color: #94a3b8;
        }

        .su-login-section {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 5px;
          flex-wrap: wrap;
          color: #64748b;
          font-size: 13px;
        }

        .su-login-link {
          color: #4f46e5;
          font-weight: 700 !important;
          cursor: pointer;
        }

        .su-login-link:hover {
          text-decoration: underline;
        }

        .su-security-note {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          margin-top: 24px;
          color: #94a3b8;
        }

        .su-security-note svg {
          font-size: 16px;
        }

        .su-security-note p {
          font-size: 11px;
        }

        .su-copyright {
          text-align: center;
          color: #cbd5e1;
          font-size: 10px !important;
          margin-top: 12px !important;
        }

        .su-mobile-brand {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          margin-bottom: 32px;
          cursor: pointer;
        }

        .su-mobile-brand p {
          font-size: 22px;
          font-weight: 800;
          color: #0f172a;
        }

        .su-mobile-logo-icon {
          width: 40px;
          height: 40px;
          border-radius: 11px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #eef2ff;
          color: #4f46e5;
        }

        /* =====================================
           TABLET
        ===================================== */

        @media (max-width: 992px) {
          .su-page {
            padding: 16px;
          }

          .su-container {
            grid-template-columns: 1fr;
            max-width: 620px;
            min-height: auto;
          }

          .su-branding {
            min-height: 280px;
          }

          .su-branding-content {
            padding: 30px;
          }

          .su-branding-text {
            margin-top: 60px;
            margin-bottom: 0;
          }

          .su-branding-title {
            font-size: 2.4rem !important;
          }

          .su-feature-list {
            display: none;
          }

          .su-form-section {
            padding: 40px 35px;
          }
        }

        /* =====================================
           MOBILE
        ===================================== */

        @media (max-width: 600px) {
          .su-page {
            min-height: 100dvh;
            padding: 0;
            align-items: stretch;
          }

          .su-container {
            min-height: 100dvh;
            border-radius: 0;
            display: block;
            box-shadow: none;
          }

          .su-branding {
            display: none;
          }

          .su-form-section {
            min-height: 100dvh;
            padding: 28px 18px;
            align-items: flex-start;
          }

          .su-form-container {
            max-width: 100%;
            padding-top: 12px;
          }

          .su-form-title {
            font-size: 29px !important;
          }

          .su-name-row {
            grid-template-columns: 1fr;
            gap: 0;
          }
        }

      `}</style>

    </Box>
  )
}
